"use client";

import { DownloadOutlineIcon, MenuIcon, XIcon } from "assets/svgs";
import axios from "axios";
import { Button } from "components/Button";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const FloatingMenu = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      setIsVisible(window.scrollY > 200);
    };

    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  // 251124 추가 - 다운로드 파일명 맵
  const FILE_NAME_MAP: Record<string, string> = {
        'brochure_iops': 'Brochure(Eng).pdf',
        'brochure_iopsspace': 'Brochure(IopsSpace).pdf'
  };

  const handleFileDownload = async (fileKey:string) => {
    try {
      const response = await axios.get(`/api/file/download?fileKey=${encodeURIComponent(fileKey)}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // 251124 추가
      const saveName = FILE_NAME_MAP[fileKey] || `${fileKey}.pdf`;

      link.setAttribute('download', saveName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('다운로드 실패:', err);
    }
  };
  
  const handleIopsSpaceDownload = () => handleFileDownload('brochure_iopsspace');
  const handleIopsDownload = () => handleFileDownload('brochure_iops');
  

  return (
    <>
      {isVisible && (
        <button
          className="fixed bottom-5 left-5 z-[500] rounded-full bg-gray-300 p-3 shadow-sm duration-300 hover:bg-slate-400 focus:outline-none"
          aria-label="Open menu"
          onClick={toggleMenu}
        >
          <MenuIcon className="h-6 w-6 text-lg font-semibold transition-colors duration-300 hover:text-white" />
        </button>
      )}
      <div
        className={`fixed inset-0 z-[600] bg-black bg-opacity-50 transition-opacity duration-300 ${
          isMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={toggleMenu}
        aria-hidden={!isMenuOpen}
      >
        <nav
          className={`fixed inset-y-0 left-0 z-[700] w-full sm:max-w-sm lg:max-w-full lg:flex lg:items-center lg:justify-center bg-white overflow-y-auto transition-all duration-300 ease-in-out transform ${
            isMenuOpen
              ? "translate-x-0 lg:translate-x-0 lg:scale-100 lg:opacity-100"
              : "-translate-x-full lg:translate-x-0 lg:scale-95 lg:opacity-0"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="lg:max-w-4xl lg:w-full lg:relative">
            <div className="flex justify-between items-center p-4 border-b lg:justify-center">
              <h2 className="text-xl font-bold">IOPS SPACE</h2>
              <button
                onClick={toggleMenu}
                className="text-gray-500 hover:text-gray-700 lg:fixed lg:bottom-5 lg:left-5 lg:z-[800]"
                aria-label="Close menu"
              >
                <XIcon className="h-6 w-6 lg:text-lg lg:font-semibold lg:transition-colors lg:duration-300 lg:hover:text-white" />
              </button>
            </div>
            <div className="p-4 space-y-6 lg:flex lg:justify-between lg:space-y-0 lg:space-x-8">
              <div className="lg:flex-1">
                <h3 className="text-lg font-semibold mb-2">Pricing</h3>
                <ul className="flex flex-col space-y-2 pl-2">
                  <li>
                    <Link to="/pricing" className="text-base hover:text-blue-500">
                      Pricing
                    </Link>
                  </li>
                  <li>
                    <Link to="/calculator" className="text-base hover:text-blue-500">
                      Calculator
                    </Link>
                  </li>
                  <li>
                    <Link to="/promotion" className="text-base hover:text-blue-500">
                      Promotion
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="lg:flex-1">
                <h3 className="text-lg font-semibold mb-2">Schedule</h3>
                <ul className="flex flex-col space-y-2 pl-2">
                  <li>
                    <Link to="/get-started" className="text-base hover:text-blue-500">
                      Get Started
                    </Link>
                  </li>
                  <li>
                    <Link to="/manage-spacecraft" className="text-base hover:text-blue-500">
                      Manage Spacecraft
                    </Link>
                  </li>
                  <li>
                    <Link to="/contact-profile" className="text-base hover:text-blue-500">
                      Contact Profile
                    </Link>
                  </li>
                  <li>
                    <Link to="/schedule-contact" className="text-base hover:text-blue-500">
                      Schedule a Contact
                    </Link>
                  </li>
                  <li>
                    <Link to="/manage-contacts" className="text-base hover:text-blue-500">
                      Manage Contacts
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="lg:flex-1">
                <h3 className="text-lg font-semibold mb-2">Support</h3>
                <ul className="flex flex-col space-y-2 pl-2">
                  <li>
                    <Link to="/faq" className="text-base hover:text-blue-500">
                      FAQ
                    </Link>
                  </li>
                  <li>
                    <Link to="/contact-us" className="text-base hover:text-blue-500">
                      Contact Us
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button className="h-9" text="IOPS SPACE" onClick={handleIopsSpaceDownload} type="secondary" size="small" icon={<DownloadOutlineIcon/>}/>
              <Button className="h-9" text="IOPS" onClick={handleIopsDownload} type="secondary" size="small" icon={<DownloadOutlineIcon/>}/>
            </div>
          </div>
        </nav>
      </div>
    </>
  );
};

export default FloatingMenu;